import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { showToast } from '../Toast';
import ConfirmModal from '../ConfirmModal';
import EndMembershipSheet from './EndMembershipSheet';
import RecordCashSheet from './RecordCashSheet';
import MemberPicker from './MemberPicker';
import { DSSheet, DSButton, DSSelect, DSInput, DSAlert } from '../../design-system/primitives';
import '../../pages/membership/membership.css';

// The Membership section of Manage User's Money tab.
//
// One rule shapes all of it: status is never a bare boolean. It is always state
// plus reason plus date. A boolean can be wrong without ever looking wrong,
// which is how the original bug survived months unnoticed.
//
// Everything here reads the derived status from the server. Nothing on this
// screen consults `users.is_member`, which is a cache and not an authority.
//
// Email addresses deliberately do NOT live here - they are in Overview under
// Contact, because they are contact details before they are payment plumbing.
// Only the Square customer records live here.

const METHOD_LABEL = {
  SUBSCRIPTION: 'Square subscription',
  INVOICE: 'Square recurring invoice',
  CASH: 'Cash at the club',
  COMP: 'Complimentary',
  SPONSORED: 'Paid by another member'
};

const METHOD_NOTE = {
  SUBSCRIPTION: 'Charged automatically by Square',
  INVOICE: 'Invoiced monthly, pays by card',
  CASH: 'Recorded by an admin each month',
  COMP: 'No payment expected',
  SPONSORED: 'Covered by another member'
};

const METHOD_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'SUBSCRIPTION', label: 'Subscription' },
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'CASH', label: 'Cash' },
  { value: 'COMP', label: 'Comp' },
  { value: 'SPONSORED', label: 'Paid by another member' }
];

const SQUARE_DASHBOARD = 'https://squareup.com/dashboard/customers';

function MembershipPanel({ userId, userName, memberSince }) {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [showEnd, setShowEnd] = useState(false);
  const [showCash, setShowCash] = useState(false);
  const [showMethod, setShowMethod] = useState(false);
  const [methodDraft, setMethodDraft] = useState('');
  const [showRenewal, setShowRenewal] = useState(false);
  const [renewalDraft, setRenewalDraft] = useState('');
  const [autoRenewalDraft, setAutoRenewalDraft] = useState(false);
  const [showSponsorPicker, setShowSponsorPicker] = useState(false);
  const [sponsorDraft, setSponsorDraft] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const response = await apiService.getMemberStatus(userId);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load membership status');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const run = async (work, message) => {
    setBusy(true);
    try {
      await work();
      if (message) showToast(message, 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.error || 'That did not work', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="membership-panel__loading">Loading membership...</p>;
  if (error) return <DSAlert variant="error">{error}</DSAlert>;
  if (!data) return null;

  const { status, evidence, sponsorship, squareCustomers = [], onboarding } = data;
  const method = data.paymentMethod;

  // Nothing on file at all. Stated as a fact rather than a problem, because
  // membership is granted automatically the moment evidence appears.
  if (!data.isMember && data.state === 'NEVER' && !method) {
    return (
      <section className="membership-panel__none">
        <p className="membership-panel__none-title">Not a member</p>
        <p className="membership-panel__none-body">
          No membership payment evidence on file. If a Square subscription or membership
          payment appears, they become a member automatically.
        </p>
      </section>
    );
  }

  const evidenceRows = buildEvidenceRows(evidence, method, sponsorship, data.endsOn, Boolean(data.isMember));
  const payments = (evidence && evidence.recentPayments) || [];
  // Membership as spells rather than one flat list. Served by the API from the
  // FULL payment set, not the twelve `recentPayments` carries, so an older
  // membership keeps its payments.
  const membershipHistory = data.membershipHistory || {};
  const spells = membershipHistory.spells || [];
  const historySummary = membershipHistory.summary || null;
  const coveredBy = (evidence && evidence.coveredBy) || [];

  // WHAT A NON-MEMBER'S RECORD SHOWS.
  //
  // Their history, and the actions that can START something. Not a payment
  // plan, not a renewal date, and not "End membership" - there is nothing to
  // end. Lauren Russell is why: she has not paid since December 2024 and her
  // record still announced "Pays by Square recurring invoice, renews on the
  // 5th" beside "Never held a membership", because `payment_method` is left on
  // the account as history and the card read it as a live arrangement.
  //
  // The earlier guard above only catches somebody with NO method at all, which
  // is why she fell through it.
  const isMember = Boolean(data.isMember);

  return (
    <section className="membership-panel">
      <header className="membership-panel__head">
        <h3>Membership</h3>
        <span className="membership-panel__price">$50 per month</span>
      </header>

      <div className="membership-panel__body">
        {/* An ending in flight goes first, because it changes what every number
            below means. */}
        {data.endsOn && (
          <div className="membership-panel__offboarding">
            <span className="membership-panel__offboarding-main">
              <span className="membership-panel__offboarding-title">
                Membership ends {formatDate(data.endsOn)}
              </span>
              <span className="membership-panel__offboarding-progress">
                Full access until then. Stop the payment now so it does not renew.
              </span>
            </span>
            <DSButton
              tone="secondary" appearance="outline" size="sm" disabled={busy}
              onClick={() => run(() => apiService.cancelEndMembership(userId), 'Ending cancelled, membership kept')}
            >
              Cancel ending
            </DSButton>
          </div>
        )}

        <div className="membership-panel__status">
          <div className="membership-panel__status-line">
            <span className={`membership-panel__pill membership-panel__pill--${pillTone(data.state)}`}>
              {recordLabel(data.state)}
            </span>
            {memberSince && (
              <span className="membership-panel__since">Member since {formatDate(memberSince)}</span>
            )}
          </div>
          <p className="membership-panel__reason">{status.reason}</p>
          {status.recordDetail && <p className="membership-panel__reason">{status.recordDetail}</p>}
        </div>

        {isMember && (
          <>
        <div className="membership-panel__method">
          <span className="membership-panel__method-main">
            <span className="membership-panel__label">Pays by</span>
            <span className="membership-panel__method-value">
              {METHOD_LABEL[method] || 'Not recorded'}
            </span>
            <span className="membership-panel__method-note">
              {METHOD_NOTE[method] || 'No expected payment method on file'}
            </span>
          </span>
          <DSButton
            tone="secondary" appearance="outline" size="sm"
            onClick={() => { setMethodDraft(method || ''); setShowMethod(true); }}
          >
            Change
          </DSButton>
        </div>

        {/* Sits with "Pays by" because it is the same class of fact: how they
            pay, and when it falls due. It is also the difference between late
            and not due yet - with no renewal day, cash cover falls back to
            month-end, so somebody who pays on the 18th reads as overdue from
            the 1st and lands in Needs attention for nine days every month. */}
        <div className="membership-panel__method">
          <span className="membership-panel__method-main">
            <span className="membership-panel__label">Renews on</span>
            <span className="membership-panel__method-value">
              {data.renewalDay ? `The ${ordinal(data.renewalDay)} of the month` : 'Not set'}
            </span>
            <span className="membership-panel__method-note">
              {renewalNote(data.renewalDay, data.autoRenewal, method)}
            </span>
          </span>
          <DSButton
            tone="secondary" appearance="outline" size="sm"
            onClick={() => {
              setRenewalDraft(data.renewalDay ? String(data.renewalDay) : '');
              setAutoRenewalDraft(Boolean(data.autoRenewal));
              setShowRenewal(true);
            }}
          >
            {data.renewalDay ? 'Change' : 'Set'}
          </DSButton>
        </div>
          </>
        )}

        <div className="membership-panel__section">
          <p className="membership-panel__label">Payment evidence</p>
          {evidenceRows.length === 0 ? (
            <p className="membership-panel__muted">Nothing on file yet.</p>
          ) : (
            <div className="membership-panel__rows">
              {evidenceRows.map((row) => (
                <div key={row.label} className="membership-panel__row">
                  <span className="membership-panel__row-label">{row.label}</span>
                  <span className="membership-panel__row-value">{row.value}</span>
                </div>
              ))}
            </div>
          )}
          <a
            className="membership-panel__square-link"
            href={squareCustomers[0]
              ? `${SQUARE_DASHBOARD}/${squareCustomers[0].squareCustomerId}`
              : SQUARE_DASHBOARD}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in Square
          </a>
        </div>

        <div className="membership-panel__section">
          <p className="membership-panel__label">Linked Square customers</p>
          {squareCustomers.length === 0 ? (
            <p className="membership-panel__muted">None linked yet.</p>
          ) : (
            squareCustomers.map((customer) => (
              <div key={customer.squareCustomerId} className="membership-panel__square">
                {/* The address Square holds is only known once a sync has
                    resolved this customer and stored it. Older links predate
                    that, so rather than leading with a missing-value message,
                    lead with the identifier we do have and say plainly why the
                    address is not there yet. */}
                <span className="membership-panel__square-main">
                  {customer.email ? (
                    <>
                      <span className="membership-panel__square-email">{customer.email}</span>
                      <span className="membership-panel__square-id">{customer.squareCustomerId}</span>
                    </>
                  ) : (
                    <>
                      <span className="membership-panel__square-email">
                        {customer.squareCustomerId}
                      </span>
                      <span className="membership-panel__square-id">
                        Square address recorded at the next sync
                      </span>
                    </>
                  )}
                </span>
                <DSButton
                  tone="secondary" appearance="outline" size="sm" disabled={busy}
                  onClick={() => setConfirmState({
                    title: 'Detach this Square customer?',
                    // Says where the payments go. "Detach" alone sounds like
                    // nothing happens to them.
                    message: `Payments made under ${customer.email || 'this customer'} will no longer count toward this membership, and past payments move back to unmatched.`,
                    confirmLabel: 'Detach customer',
                    danger: true,
                    onConfirm: () => run(
                      () => apiService.detachSquareCustomer(customer.squareCustomerId),
                      'Square customer detached'
                    )
                  })}
                >
                  Detach
                </DSButton>
              </div>
            ))
          )}
          <p className="membership-panel__muted">Email addresses live in Overview, under Contact.</p>
          <DSButton
            tone="secondary" appearance="outline" size="sm"
            onClick={() => navigate(`/users?tab=reconcile&source=${userId}`)}
          >
            Merge with another account
          </DSButton>
        </div>

        {/* Admin only, and a hard constraint: somebody whose membership is a
            gift must never have that surfaced back at them. This panel is only
            ever rendered on the admin screen.

            Always shown, rather than only when a sponsorship already exists.
            Removing one clears the payment method, which used to take the whole
            section off the screen with it - so an admin who removed a
            sponsorship by accident had no way to put it back, and no way to
            record a new one on anybody else. */}
        <div className="membership-panel__section">
          <p className="membership-panel__label">Sponsorship, admins only</p>
          {sponsorship && sponsorship.past && sponsorship.past.length > 0 && (
            <ul className="membership-panel__past">
              {sponsorship.past.map((entry, index) => (
                <li key={index}>
                  {entry.direction === 'paid-for'
                    ? `Was paid for by ${entry.sponsorName}`
                    : `Paid for ${entry.beneficiaryName}`}
                  <span className="membership-panel__past-when">{sponsorshipPeriod(entry)}</span>
                </li>
              ))}
            </ul>
          )}

          {sponsorship && sponsorship.sponsoredBy ? (
            <div className="membership-panel__square">
              <span className="membership-panel__square-main">
                <span className="membership-panel__square-email">
                  Paid for by {sponsorship.sponsoredBy.name}
                </span>
                <span className="membership-panel__square-meta">
                  {sponsorshipPeriod(sponsorship.sponsoredBy)}
                </span>
              </span>
              <DSButton
                tone="secondary" appearance="outline" size="sm" disabled={busy}
                onClick={() => setConfirmState({
                  title: 'Remove this sponsorship?',
                  // Says what actually changes. "Remove" on its own reads like
                  // it might end their membership.
                  message: 'They keep their membership and their door access, but nothing '
                    + 'will be paying for it. They will show as needing attention until a '
                    + 'payment method is recorded.',
                  confirmLabel: 'Remove sponsorship',
                  onConfirm: () => run(() => apiService.unlinkSponsorship(userId), 'Sponsorship removed')
                })}
              >
                Remove
              </DSButton>
            </div>
          ) : (
            <DSButton tone="secondary" appearance="outline" size="sm" onClick={() => setShowSponsorPicker(true)}>
              Someone else pays for this
            </DSButton>
          )}
          {sponsorship && (sponsorship.paying || []).map((person) => (
            <p key={person.userId} className="membership-panel__muted">
              Also pays for {person.name}
              {sponsorshipPeriod(person) && ` - ${sponsorshipPeriod(person).toLowerCase()}`}
            </p>
          ))}
        </div>

        {onboarding && (onboarding.outstanding || []).length > 0 && (
          <DSAlert variant="info" appearance="soft">
            Paying, onboarding incomplete. Outstanding: {onboarding.outstanding.join(', ')}.
            <DSButton
              tone="secondary" appearance="ghost" size="sm" disabled={busy}
              onClick={() => run(() => apiService.grantOnboardingOverride(userId), 'Membership granted')}
            >
              Grant membership anyway
            </DSButton>
          </DSAlert>
        )}

        <div className="membership-panel__actions">
          {method === 'CASH' && (
            <DSButton
              tone="primary" appearance="solid"
              className="membership-panel__cash"
              onClick={() => setShowCash(true)}
            >
              Record cash payment
            </DSButton>
          )}
          <div className="membership-panel__action-pair">
            {method === 'COMP' ? (
              <DSButton
                tone="secondary" appearance="outline" disabled={busy}
                onClick={() => run(() => apiService.revokeComp(userId), 'Complimentary membership removed')}
              >
                Remove comp
              </DSButton>
            ) : (
              <DSButton
                tone="secondary" appearance="outline" disabled={busy}
                onClick={() => setConfirmState({
                  title: 'Grant complimentary membership?',
                  message: 'They stay a member with no payment expected. Who granted it and when is recorded.',
                  confirmLabel: 'Grant comp',
                  onConfirm: () => run(() => apiService.grantComp(userId), 'Complimentary membership granted')
                })}
              >
                Grant comp
              </DSButton>
            )}
            {/* Nothing to end for somebody who is not a member. Offering it
                invites an admin to "end" a membership that stopped months ago,
                which writes an ending date that never happened. */}
            {!data.endsOn && isMember && (
              <DSButton tone="danger" appearance="outline" onClick={() => setShowEnd(true)}>
                End membership
              </DSButton>
            )}
          </div>
        </div>
      </div>

      {/* MEMBERSHIP HISTORY, grouped into spells.
          People leave and come back - Christopher Cannon's job takes him out of
          the state - and a flat list of payments cannot show that. Each spell
          carries the payments that belong to it, so "when were they a member
          and what did they pay for it" is one question with one answer.
          Any missing month ends a spell: if a membership is running, that
          amount is due every month. */}
      <div className="membership-panel__history">
        <p className="membership-panel__label membership-panel__label--bar">Membership history</p>

        {spells.length === 0 ? (
          <p className="membership-panel__muted membership-panel__muted--pad">
            {payments.length === 0 && coveredBy.length === 0
              ? 'Never held a membership.'
              : 'No membership months recorded yet.'}
          </p>
        ) : (
          <>
            {historySummary && historySummary.spellCount > 1 && (
              <p className="membership-panel__muted membership-panel__muted--pad">
                {historySummary.spellCount} memberships
                {' · '}{historySummary.totalMonths} months in total
                {historySummary.firstJoined && `, first joined ${formatMonth(historySummary.firstJoined)}`}
              </p>
            )}

            {spells.map((spell) => (
              <div key={`${spell.from}-${spell.to}`} className="membership-panel__spell">
                <div className="membership-panel__spell-head">
                  <span className="membership-panel__spell-range">
                    {formatMonth(spell.from)}
                    {spell.to !== spell.from && ` to ${spell.isCurrent ? 'now' : formatMonth(spell.to)}`}
                    {spell.isCurrent && spell.to === spell.from && ' (current)'}
                  </span>
                  <span className="membership-panel__spell-meta">
                    {spell.months} {spell.months === 1 ? 'month' : 'months'}
                    {spell.comp && ' · complimentary'}
                    {spell.totalPaid > 0 && ` · $${spell.totalPaid.toFixed(2)}`}
                  </span>
                </div>

                {spell.payments.length === 0 ? (
                  <p className="membership-panel__muted membership-panel__muted--pad">
                    No payments. Complimentary membership.
                  </p>
                ) : spell.payments.map((payment, index) => (
                  <div key={payment.id || `${spell.from}-${index}`} className="membership-panel__payment">
                    <span className="membership-panel__payment-main">
                      <span className="membership-panel__payment-month">
                        {formatMonth(payment.month)}
                        {/* Somebody else's money covering this member. Counted
                            once, against the payer - requirement 9.7. */}
                        {payment.paidBySomebodyElse && payment.paidByName && (
                          <span className="membership-panel__payment-covers">
                            paid by {payment.paidByName}
                          </span>
                        )}
                        {payment.coversName && (
                          <span className="membership-panel__payment-covers">
                            for {payment.coversName}
                          </span>
                        )}
                      </span>
                      <span className="membership-panel__payment-note">
                        {payment.paidBySomebodyElse
                          ? `Not their money. Counted once, against ${payment.paidByName}.`
                          : paymentNote(payment)}
                      </span>
                    </span>
                    <span className="membership-panel__payment-source">{sourceLabel(payment.source, payment.method)}</span>
                    <span className="membership-panel__payment-amount">
                      {payment.amount == null ? 'None' : `$${Number(payment.amount).toFixed(2)}`}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </div>

      {showRenewal && (
        <DSSheet
          title="When dues fall due"
          onClose={() => setShowRenewal(false)}
          footer={(
            <div className="offboard__footer">
              <DSButton
                appearance="solid" tone="primary" className="offboard__confirm" disabled={busy}
                onClick={() => {
                  const day = renewalDraft === '' ? null : Number(renewalDraft);
                  setShowRenewal(false);
                  run(
                    () => apiService.updateMembershipSettings(userId, {
                      renewalDayOfMonth: day,
                      autoRenewal: autoRenewalDraft
                    }),
                    'Renewal date saved'
                  );
                }}
              >
                Save
              </DSButton>
              <DSButton appearance="outline" tone="neutral" onClick={() => setShowRenewal(false)}>
                Cancel
              </DSButton>
            </div>
          )}
        >
          <p className="offboard__body">
            The day of the month their dues fall. Without it, cover is assumed to run to
            month-end, so somebody who pays on the 18th looks overdue from the 1st.
          </p>
          <DSInput
            id="renewal-day"
            type="number"
            min="1"
            max="31"
            label="Renewal day of month"
            placeholder="e.g. 18"
            value={renewalDraft}
            onChange={(e) => setRenewalDraft(e.target.value)}
          />
          {/* Only meaningful where Square charges them without anybody doing
              anything. A cash member is never charged automatically, so the
              checkbox is not offered to them. */}
          {AUTO_RENEWAL_METHODS.includes(method) && (
            <label className="membership-panel__check">
              <input
                type="checkbox"
                checked={autoRenewalDraft}
                onChange={(e) => setAutoRenewalDraft(e.target.checked)}
              />
              <span>
                Square charges them automatically
                <span className="membership-panel__check-note">
                  Leave this off if somebody has to collect or send an invoice each month.
                </span>
              </span>
            </label>
          )}
        </DSSheet>
      )}

      {showMethod && (
        <DSSheet
          title="Change payment method"
          onClose={() => setShowMethod(false)}
          footer={(
            <div className="offboard__footer">
              <DSButton
                appearance="solid" tone="primary" className="offboard__confirm" disabled={busy}
                onClick={() => {
                  setShowMethod(false);
                  run(() => apiService.setPaymentMethod(userId, methodDraft || null), 'Payment method updated');
                }}
              >
                Save method
              </DSButton>
              <DSButton appearance="outline" tone="neutral" onClick={() => setShowMethod(false)}>
                Cancel
              </DSButton>
            </div>
          )}
        >
          {/* Worth saying plainly: Square cannot report a recurring invoice plan
              at all, so this field is the only way the system knows to expect one. */}
          <p className="offboard__body">
            This is how the club expects them to pay. Square cannot report an invoice
            plan, so recording it here is the only way we know to expect one.
          </p>
          <DSSelect
            id="method-select"
            label="Expected payment method"
            value={methodDraft}
            onChange={(event) => setMethodDraft(event.target.value)}
          >
            {METHOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </DSSelect>
          <p className="offboard__note">
            Any healthy evidence keeps them a member, whichever method is set.
          </p>
        </DSSheet>
      )}

      {showEnd && (
        <EndMembershipSheet
          userId={userId}
          name={userName}
          method={method}
          endsOn={data.endsOn || renewalGuess(evidence, method)}
          onClose={() => setShowEnd(false)}
          onDone={() => { setShowEnd(false); load(); }}
        />
      )}

      {showCash && (
        <RecordCashSheet
          member={{
            userId,
            name: userName,
            detail: evidence.cashPaidThrough
              ? `Paid through ${formatMonth(evidence.cashPaidThrough)}.`
              : 'No cash payment recorded yet.',
            // Their own months only. A payment they made for somebody else
            // does not cover them.
            paidMonths: (evidence.recentPayments || [])
              .filter((payment) => !payment.coversUserId)
              .map((payment) => String(payment.month).slice(0, 7))
          }}
          onClose={() => setShowCash(false)}
          onDone={() => { setShowCash(false); load(); }}
        />
      )}

      {showSponsorPicker && (
        <MemberPicker
          title="Who pays for this membership?"
          description="This stays between admins. Nothing is shown to either member."
          excludeUserId={userId}
          onClose={() => setShowSponsorPicker(false)}
          // Picking the person is only half of it. Without a period the record
          // reads as though it has always run, and an arrangement that ended
          // two years ago cannot be recorded at all - which is the case that
          // cannot be reconstructed later.
          onPick={(user) => {
            setShowSponsorPicker(false);
            setSponsorDraft({ user, startsOn: '', endsOn: '' });
          }}
        />
      )}

      {sponsorDraft && (
        <DSSheet
          title={`When did ${sponsorDraft.user.first_name || 'they'} start paying?`}
          onClose={() => setSponsorDraft(null)}
          footer={(
            <div className="offboard__footer">
              <DSButton
                appearance="solid" tone="primary" className="offboard__confirm"
                disabled={busy || !sponsorDraft.startsOn}
                onClick={() => {
                  const draft = sponsorDraft;
                  setSponsorDraft(null);
                  run(
                    () => apiService.linkSponsorship(userId, draft.user.id, {
                      startsOn: draft.startsOn,
                      endsOn: draft.endsOn || null
                    }),
                    'Sponsorship recorded'
                  );
                }}
              >
                Record it
              </DSButton>
              <DSButton appearance="outline" tone="neutral" onClick={() => setSponsorDraft(null)}>
                Cancel
              </DSButton>
            </div>
          )}
        >
          <p className="offboard__body">
            The month their payments started covering this membership. Leave the end date
            empty if it is still running.
          </p>
          <DSInput
            id="sponsor-starts-on"
            type="date"
            label="Started"
            value={sponsorDraft.startsOn}
            onChange={(e) => setSponsorDraft({ ...sponsorDraft, startsOn: e.target.value })}
          />
          <DSInput
            id="sponsor-ends-on"
            type="date"
            label="Ended, if it has"
            value={sponsorDraft.endsOn}
            onChange={(e) => setSponsorDraft({ ...sponsorDraft, endsOn: e.target.value })}
          />
        </DSSheet>
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          onConfirm={() => { const go = confirmState.onConfirm; setConfirmState(null); go(); }}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </section>
  );
}

/**
 * The evidence behind the status, as label/value rows.
 *
 * Invoice health carries an explicit confidence row, because Square exposes no
 * API for recurring invoice series at all. We infer the plan from individual
 * invoices, and the screen must not overstate that.
 *
 * FOR A NON-MEMBER, ONLY THE SQUARE STATUS SURVIVES.
 *
 * The rest of these rows answer "is their billing healthy", which is not a
 * question about somebody who has no billing. Kendra Corson's record showed
 * "Next charge May 23, 2026" for a cancelled subscription on a person who is
 * not a member - a date the club will never charge, presented as though it
 * would. "Latest invoice" and "Confidence" are the same: reasoning about a plan
 * that has stopped. Her payment history is in the membership history below,
 * which is where a past member's record belongs.
 */
function buildEvidenceRows(evidence, method, sponsorship, endsOn, isMember = true) {
  const rows = [];
  if (!evidence) return rows;

  // Cancelled is worth saying - it is the reason there is no plan. Anything
  // that reads as an ongoing arrangement is not.
  if (!isMember) {
    const sub = (evidence.subscriptions || [])[0];
    return sub ? [{ label: 'Square status', value: friendlySubscription(sub.status) }] : [];
  }

  const subscription = (evidence.subscriptions || [])[0];
  if (subscription) {
    rows.push({ label: 'Square status', value: friendlySubscription(subscription.status) });
    if (subscription.planName) rows.push({ label: 'Plan', value: subscription.planName });
    if (subscription.chargedThroughDate) {
      rows.push({
        label: endsOn ? 'Ends' : 'Next charge',
        value: formatDate(endsOn || subscription.chargedThroughDate)
      });
    }
  }

  // Invoice evidence, but only for somebody actually billed by invoice.
  //
  // This used to render whenever ANY invoice existed. Bruce Casassa is a cash
  // member who once paid a single Square invoice - a birthday gift covering
  // somebody else's membership - and his record showed "Latest invoice",
  // "Next scheduled Jun 24, 2026" and "Inferred from individual invoices"
  // directly above "Paid through December 2026". Three rows about a billing
  // arrangement he does not have, one of them a scheduled date months in the
  // past, contradicting the row beneath it.
  //
  // The method is the test, and it is the same signal `inferMethod` uses: an
  // invoice member is somebody with a live invoice plan, not somebody with an
  // invoice in their history.
  const invoice = method === 'INVOICE' ? (evidence.invoices || [])[0] : null;
  if (invoice) {
    rows.push({
      label: 'Latest invoice',
      value: `${friendlyInvoice(invoice.status)}${invoice.paidAt ? `, ${formatDate(invoice.paidAt)}` : ''}`
    });

    // Only a date still ahead is "next". A due date in the past is the last one
    // that came, and calling it the next one is how a settled record reads as
    // an upcoming payment.
    if (invoice.dueDate && new Date(invoice.dueDate) >= new Date()) {
      rows.push({ label: 'Next scheduled', value: formatDate(invoice.dueDate) });
    }

    rows.push({ label: 'Confidence', value: 'Inferred from individual invoices' });
  }

  if (evidence.cashPaidThrough) {
    rows.push({
      label: 'Paid through',
      value: `${formatMonth(evidence.cashPaidThrough)}${endsOn ? ', not renewing' : ''}`
    });
  }

  if (evidence.comp && evidence.comp.grantedAt) {
    if (evidence.comp.grantedBy) rows.push({ label: 'Granted by', value: evidence.comp.grantedBy });
    rows.push({ label: 'Granted on', value: formatDate(evidence.comp.grantedAt) });
  }

  if (method === 'SPONSORED' && sponsorship && sponsorship.sponsoredBy) {
    rows.push({ label: 'Paid by', value: sponsorship.sponsoredBy.name });
    const period = sponsorshipPeriod(sponsorship.sponsoredBy);
    if (period) rows.push({ label: 'Covered', value: period });
  }

  return rows;
}

/** Where "ends at renewal" would fall, from whatever evidence there is. */
function renewalGuess(evidence, method) {
  if (!evidence) return null;
  if (method === 'CASH' && evidence.cashPaidThrough) {
    const paid = new Date(evidence.cashPaidThrough);
    return new Date(Date.UTC(paid.getUTCFullYear(), paid.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
  }
  const subscription = (evidence.subscriptions || [])[0];
  if (subscription && subscription.chargedThroughDate) return subscription.chargedThroughDate;
  const invoice = (evidence.invoices || []).find((i) => i.dueDate);
  return invoice ? invoice.dueDate : null;
}

function recordLabel(state) {
  switch (state) {
    case 'ACTIVE': return 'Member';
    case 'AT_RISK': return 'Member, payment needs attention';
    case 'ONBOARDING': return 'Paying, onboarding incomplete';
    default: return 'Not a current member';
  }
}

// Only a method Square bills on its own can auto-renew. A cash member is never
// charged automatically, which is exactly why somebody has to go and collect.
const AUTO_RENEWAL_METHODS = ['SUBSCRIPTION', 'INVOICE'];

function ordinal(day) {
  const n = Number(day);
  if (!n) return '';
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`;
}

function renewalNote(renewalDay, autoRenewal, method) {
  if (!renewalDay) {
    return 'Not recorded, so cover is assumed to run to the end of each month';
  }
  if (!AUTO_RENEWAL_METHODS.includes(method)) {
    return 'Somebody collects this each month';
  }
  return autoRenewal
    ? 'Square charges them automatically'
    : 'Square does not charge automatically, so somebody follows up';
}

// "Since April 2026" or "March to July 2026". Months, not days: the club thinks
// about membership a month at a time, and a start date of the 1st is an
// artefact of how the period was derived rather than a fact about the day.
function sponsorshipPeriod(entry) {
  if (!entry || !entry.startsOn) return '';
  const from = formatMonth(entry.startsOn);
  if (!entry.endsOn) return `Since ${from}`;
  const to = formatMonth(entry.endsOn);
  return from === to ? from : `${from} to ${to}`;
}

function pillTone(state) {
  switch (state) {
    case 'ACTIVE': return 'success';
    case 'AT_RISK': return 'warning';
    case 'ONBOARDING': return 'info';
    default: return 'neutral';
  }
}

// Never show a raw Square status code to a user.
function friendlySubscription(status) {
  switch (status) {
    case 'ACTIVE': return 'Active';
    case 'PENDING': return 'Starts next cycle';
    case 'PAUSED': return 'Paused';
    case 'CANCELED': return 'Cancelled';
    case 'DEACTIVATED': return 'Deactivated by Square';
    default: return 'Unknown';
  }
}

function friendlyInvoice(status) {
  switch (status) {
    case 'PAID': return 'Paid';
    case 'PAYMENT_PENDING': return 'Payment processing';
    case 'PARTIALLY_PAID': return 'Partly paid';
    case 'SCHEDULED': return 'Scheduled';
    case 'UNPAID': return 'Awaiting payment';
    case 'CANCELED': return 'Cancelled';
    default: return 'Unknown';
  }
}

function sourceLabel(source, method) {
  // Recorded by hand like cash, but paid out of somebody's club credits.
  if (method === 'credits') return 'CREDITS';
  switch (source) {
    case 'cash': return 'CASH';
    case 'square_subscription': return 'SUBSCRIPTION';
    case 'square_invoice': return 'INVOICE';
    case 'square_order': return 'SQUARE';
    case 'comp': return 'COMP';
    default: return 'MANUAL';
  }
}

function paymentNote(payment) {
  // "Paid from Pat Payer's club credits", written when it was recorded. Says
  // whose money it was, which is the whole point of the row.
  if (payment.method === 'credits' && payment.note) return payment.note;
  if (payment.collectedByName) return `Recorded by ${payment.collectedByName}`;
  if (payment.paidAt) return `Charged ${formatDate(payment.paidAt)}`;
  return 'Recorded';
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function formatMonth(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

// Exported for tests. The evidence rows are where a member's record can quietly
// contradict itself, so they are worth pinning directly rather than through the
// whole panel.
export { buildEvidenceRows };

export default MembershipPanel;
